import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { db } from '../../services/firebase';
import { collection, query, where, getDocsFromServer, getDocs } from 'firebase/firestore';
import { ADMIN_EMAIL_ALLOWLIST, isAllowlistedAdminUsername } from '../../utils/adminAccess';

const PAID_STATUSES = ['Active', 'active', 'completed', 'succeeded'];

// Internal staff/comp accounts that skip the Stripe payment check.
// Explicit lowercased allowlist (NOT a domain wildcard) so an unverified
// signup at some @jtncgroup.com address cannot grant itself free access.
// Desk admins must be included here too — /admin sits behind this guard, so an
// admin allowlist alone still redirects unpaid desk accounts to /pricing.
const STAFF_BYPASS_EMAILS = Object.freeze([
  ...new Set([...ADMIN_EMAIL_ALLOWLIST, 'jay@jtncgroup.com']),
]);

const PaymentVerificationGuard = ({ children }) => {
  const { user, userProfile, loading } = useAuthContext();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isChecking, setIsChecking] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!user?.email) {
        console.log('❌ No user email found, skipping payment check');
        setIsChecking(false);
        return;
      }

      try {
        const normalizedEmail = user.email.trim().toLowerCase();
        console.log('🔍 PaymentVerificationGuard: Checking payment status for:', normalizedEmail);

        // Internal staff bypass — grant access without a Stripe record.
        if (
          STAFF_BYPASS_EMAILS.includes(normalizedEmail) ||
          isAllowlistedAdminUsername(userProfile?.username)
        ) {
          console.log('✅ Internal staff account — granting access without payment check:', normalizedEmail);
          setPaymentStatus({
            isPaid: true,
            purchaseKey: 'staff',
            paymentStatus: 'staff_bypass',
            amount: null,
            email: user.email,
          });
          setIsChecking(false);
          return;
        }

        // Query tbl_stripe_payment_history for user's email
        // Use getDocsFromServer to bypass stale Firestore cache, fall back to getDocs if offline
        const serverQuery = (q) =>
          getDocsFromServer(q).catch(() => getDocs(q));

        let snap = await serverQuery(query(collection(db, 'tbl_stripe_payment_history'), where('email', '==', normalizedEmail)));
        console.log('🔍 First query result (email field):', { size: snap.size, empty: snap.empty });

        if (snap.empty) {
          console.log('🔍 Trying customerEmail field...');
          snap = await serverQuery(query(collection(db, 'tbl_stripe_payment_history'), where('customerEmail', '==', normalizedEmail)));
          console.log('🔍 Second query result (customerEmail field):', { size: snap.size, empty: snap.empty });
        }

        if (!snap.empty) {
          const docs = snap.docs.sort((a, b) => {
            const dateA = (a.data().created_at?.toDate() || new Date(0)).getTime();
            const dateB = (b.data().created_at?.toDate() || new Date(0)).getTime();
            return dateB - dateA;
          });
          console.log('🔍 Found', docs.length, 'payment records for', normalizedEmail);

          // Find the most recent record with a paid status
          const activeRecords = docs.filter(doc => {
            const data = doc.data();
            const status = data.payment_status || data.metadata?.payment_status;
            return PAID_STATUSES.includes(status);
          });

          const paymentData = activeRecords.length > 0
            ? activeRecords[0].data()
            : docs[0].data();

          console.log('🔍 Using payment record:', {
            email: paymentData.email || paymentData.customerEmail,
            payment_status: paymentData.payment_status,
            purchase_key: paymentData.purchase_key
          });

          const topLevelStatus = paymentData.payment_status;
          const metadataStatus = paymentData.metadata?.payment_status;
          const finalPaymentStatus = topLevelStatus || metadataStatus;
          const isPaid = PAID_STATUSES.includes(finalPaymentStatus);

          console.log('🔍 Payment verification result:', { isPaid, finalPaymentStatus });

          setPaymentStatus({
            isPaid,
            purchaseKey: paymentData.purchase_key,
            paymentStatus: finalPaymentStatus,
            amount: paymentData.amount || paymentData.subs_amount,
            email: paymentData.email || paymentData.customerEmail
          });
        } else {
          console.log('❌ No payment data found for:', normalizedEmail);
          setPaymentStatus({
            isPaid: false,
            purchaseKey: null,
            paymentStatus: 'not_found',
            amount: null,
            email: user.email
          });
        }
      } catch (error) {
        console.error('❌ Error checking payment status:', error);
        setError('Failed to verify payment status');
        setPaymentStatus({
          isPaid: false,
          purchaseKey: null,
          paymentStatus: 'error',
          amount: null,
          email: user.email
        });
      } finally {
        setIsChecking(false);
      }
    };

    checkPaymentStatus();
  }, [user?.email, userProfile?.username]);

  // Show loading while checking
  if (loading || isChecking) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <div className="text-white">Verifying payment status...</div>
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If payment verification failed, show error with retry
  if (error) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-white mb-4">Payment Verification Error</h2>
          <p className="text-gray-300 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // If payment not found or not paid, redirect to pricing page
  if (!paymentStatus?.isPaid) {
    console.log('🔒 Payment not verified, redirecting to pricing page:', paymentStatus?.paymentStatus);
    return <Navigate to="/pricing" replace />;
  }

  // If payment is verified, render the protected content
  console.log('✅ Payment verified for:', user.email);
  return children;
};

export default PaymentVerificationGuard;
